import React, { useEffect, useRef } from "react";
import { selectFormLanguage } from "redux/form/form.selector";
import { useAppDispatch, useAppSelector } from "utils/hooks";
import GlobalStyle from "global";
import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { AppContainer } from "app.styles";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "components/appinsights/appinsights";

import Header from "components/header/header.component";
import DonationPage from "pages/donation/donation-page.component";
import ThankYouPage from "pages/thank-you/thank-you.component";
import Popup from "components/popup/popup.component";
import ErrorPage from "pages/error-page/error-page.component";
import {
  selectIsFormDisabled,
  selectSession,
  selectViewportHeight,
} from "redux/helpers/helpers.selector";
import ProtectedRoute from "components/protected-route/protected-route.component";
import { updateViewportHeight } from "redux/helpers/helpers.reducer";
import SessionManager from "components/session-manager/session-manager.component";
import { FormSourceType } from "utils/interfaces";
import LanguageManager from "components/language-manager/language-manager.component";
import datalayerAPI from "utils/datalayerAPI";
import PaymentManager from "components/payment-manager/payment-manager.component";

const App: React.FC = () => {
  // URL params
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source") || searchParams.get("s_cscid");

  // GLOBAL STATE
  const queryString = useLocation().search;
  const language = useAppSelector(selectFormLanguage);
  const isFormDisabled = useAppSelector(selectIsFormDisabled);
  const viewportHeight = useAppSelector(selectViewportHeight);
  const session = useAppSelector(selectSession);
  const dispatch = useAppDispatch();

  // LOCAL STATE
  const isOverlayForm = source === FormSourceType.OVERLAY_FORM;

  const sentRef = useRef(false);

  useEffect(() => {
    const resizeListener = () => {
      dispatch(updateViewportHeight(window.innerHeight));
    };
    window.addEventListener("resize", resizeListener);

    return () => {
      window.removeEventListener("resize", resizeListener);
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!session.sessionID || sentRef.current) return;
    sentRef.current = true;

    datalayerAPI.handleDonationAttemptStarted(session.sessionID);
  }, [session.sessionID]);

  return (
    <AppContainer isFormDisabled={isFormDisabled} className="App">
      <AppInsightsContext.Provider value={reactPlugin}>
        <GoogleReCaptchaProvider
          reCaptchaKey={process.env.REACT_APP_RE_CAPTCHA_SITE_KEY}
          useEnterprise={true}
          language={language}
        >
          <GlobalStyle isOverlayForm={isOverlayForm} />
          <SessionManager />
          <LanguageManager />
          <PaymentManager />
          {!isOverlayForm && <Header />}
          <Routes>
            <Route
              path="/"
              element={<DonationPage viewportHeight={viewportHeight} />}
            />
            <Route
              path="/thank-you"
              element={<ThankYouPage viewportHeight={viewportHeight} />}
            />
            <Route
              path="/error"
              element={
                <ProtectedRoute>
                  <ErrorPage viewportHeight={viewportHeight} />
                </ProtectedRoute>
              }
            />
            <Route path="/.auth/logout" element={<p>Loggin out...</p>} />
            <Route
              path="*"
              element={<Navigate to={`/${queryString}`} replace />}
            />
          </Routes>
          <Popup />
        </GoogleReCaptchaProvider>
      </AppInsightsContext.Provider>
    </AppContainer>
  );
};

export default App;
